import { useCallback, useEffect, useState } from "react";
import { Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");
const dstr = (d) => d.toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => dstr(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return dstr(d); };
const lbl = (s) => (s || "").replace(/_/g, " ");
const PL = ({ m }) => <Badge className={Number(m) >= 0 ? "bg-emerald-600" : "bg-red-500"}>{Number(m) >= 0 ? "Profit" : "Loss"}</Badge>;

const MAT_ORDER = ["GRN_PENDING", "GRN_COMPLETED", "DEFECTIVE_UNDER_REVIEW", "SENT_BACK_TO_CLIENT", "ACCEPTED_BY_CLIENT",
  "ASSIGNED_TO_FIELD_USER", "ACKNOWLEDGED_BY_FIELD_USER", "RETURN_REQUESTED", "INSTALLED_BY_FIELD_USER", "COMPLETED_DATA_ENTRY"];
const orderedKeys = (obj) => { const keys = Object.keys(obj || {}); return [...MAT_ORDER.filter((k) => k in (obj || {})), ...keys.filter((k) => !MAT_ORDER.includes(k)).sort()]; };

export default function CeoSummary() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);

  const applyRange = (f, t) => { setFrom(f); setTo(t); };
  const presets = [
    { id: "today", label: "Today", run: () => applyRange(today(), today()) },
    { id: "yesterday", label: "Yesterday", run: () => applyRange(daysAgo(1), daysAgo(1)) },
    { id: "daybefore", label: "Day Before Yesterday", run: () => applyRange(daysAgo(2), daysAgo(2)) },
    { id: "month", label: "Current Month", run: () => applyRange(monthStart(), today()) },
  ];

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/ceo-summary", { params: { date_from: from, date_to: to } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load CEO summary"))
      .finally(() => setLoading(false));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const openProject = (p) => {
    setDetail(null); setDetailLoading(true); setTypeFilter(null);
    api.get(`/reports/ceo-summary/project/${p.project_id}`, { params: { date_from: from, date_to: to } })
      .then((r) => setDetail(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Failed to load project detail"); setDetailLoading(false); setDetail(null); })
      .finally(() => setDetailLoading(false));
  };
  const closeDetail = () => { setDetail(null); setDetailLoading(false); setTypeFilter(null); };
  const detailOpen = detailLoading || !!detail;

  const t = data?.totals || {};
  const g = detail?.financial?.grand || {};

  const TotCard = ({ label, value, testid, extra }) => (
    <div className="rounded-xl border bg-white p-3 text-center" data-testid={testid}>
      <div className="text-lg font-bold text-slate-800">{value}</div>
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      {extra}
    </div>
  );

  return (
    <div>
      <PageHeader title="CEO Summary" subtitle="All projects at a glance — materials, DPR activity & financials" />
      <div className="mb-4 flex flex-wrap items-end gap-2" data-testid="ceo-filters">
        <div><Label className="text-xs">From</Label><Input type="date" className="mt-1" data-testid="ceo-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label className="text-xs">To</Label><Input type="date" className="mt-1" data-testid="ceo-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <Button size="sm" className="h-9 bg-teal-700 hover:bg-teal-800" data-testid="ceo-apply" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
        </Button>
        <div className="flex flex-wrap items-center gap-1.5">
          {presets.map((p) => (
            <Button key={p.id} size="sm" variant="outline" className="h-9" data-testid={`ceo-preset-${p.id}`} onClick={p.run} disabled={loading}>{p.label}</Button>
          ))}
          <Button size="sm" variant="ghost" className="h-9 text-slate-500" data-testid="ceo-clear" onClick={() => applyRange(monthStart(), today())} disabled={loading}>Clear</Button>
        </div>
      </div>

      {data && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="ceo-totals">
          <TotCard label="Projects" value={num(t.projects)} testid="ceo-total-projects" />
          <TotCard label="Materials" value={num(t.materials)} testid="ceo-total-materials" />
          <TotCard label="DPR Activities" value={num(t.activity)} testid="ceo-total-activity" />
          <TotCard label="Revenue" value={inr(t.revenue)} testid="ceo-total-revenue" />
          <TotCard label="Cost" value={inr(t.cost)} testid="ceo-total-cost" />
          <TotCard label="Margin" value={inr(t.margin)} testid="ceo-total-margin" extra={<div className="mt-1"><PL m={t.margin} /></div>} />
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border bg-white" data-testid="ceo-table">
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500">
            <th className="px-3 py-2">Project</th><th className="px-3 py-2 text-right">Materials</th>
            <th className="px-3 py-2 text-right">Activities</th><th className="px-3 py-2 text-right">Revenue</th>
            <th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Margin</th>
            <th className="px-3 py-2 text-center">P/L</th><th className="px-3 py-2 text-right">Billing (Billed/Pending)</th>
          </tr></thead>
          <tbody>
            {(data?.projects || []).map((p) => (
              <tr key={p.project_id} className="cursor-pointer border-b last:border-0 hover:bg-teal-50" data-testid={`ceo-row-${p.project_id}`} onClick={() => openProject(p)}>
                <td className="px-3 py-2"><span className="font-medium text-teal-700">{p.project_code ? `${p.project_code} — ` : ""}{p.project_name}</span></td>
                <td className="px-3 py-2 text-right">{num(p.materials_total)}</td>
                <td className="px-3 py-2 text-right">{num(p.activity_total)}</td>
                <td className="px-3 py-2 text-right text-teal-700">{inr(p.revenue)}</td>
                <td className="px-3 py-2 text-right text-slate-600">{inr(p.cost)}</td>
                <td className={`px-3 py-2 text-right ${Number(p.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(p.margin)}</td>
                <td className="px-3 py-2 text-center"><PL m={p.margin} /></td>
                <td className="px-3 py-2 text-right">{num(p.billed)} / {num(p.pending_billing)}</td>
              </tr>
            ))}
            {data && !data.projects.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">No projects in scope.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={detailOpen} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ceo-detail">
          <DialogHeader>
            <DialogTitle className="font-display">
              {detail ? `${detail.project.project_code ? detail.project.project_code + " — " : ""}${detail.project.project_name}` : "Loading…"}
              <span className="ml-2 text-xs font-normal text-slate-500">{detail?.date_from} → {detail?.date_to}</span>
            </DialogTitle>
          </DialogHeader>
          {detailLoading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>}
          {detail && (
            <>
              <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="ceo-detail-head">
                <div className="rounded-lg border bg-slate-50 p-2 text-center"><div className="text-base font-bold">{num(detail.materials.total)}</div><div className="text-[10px] uppercase text-slate-500">Materials</div></div>
                <div className="rounded-lg border bg-slate-50 p-2 text-center"><div className="text-base font-bold text-teal-700">{inr(g.revenue)}</div><div className="text-[10px] uppercase text-slate-500">Revenue</div></div>
                <div className="rounded-lg border bg-slate-50 p-2 text-center"><div className="text-base font-bold">{inr(g.cost)}</div><div className="text-[10px] uppercase text-slate-500">Cost</div></div>
                <div className="rounded-lg border bg-slate-50 p-2 text-center"><div className={`text-base font-bold ${Number(g.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(g.margin)}</div><div className="mt-0.5"><PL m={g.margin} /></div></div>
              </div>
              <Tabs defaultValue="lifecycle">
                <TabsList>
                  <TabsTrigger value="lifecycle" data-testid="ceo-tab-lifecycle">Material Lifecycle</TabsTrigger>
                  <TabsTrigger value="days" data-testid="ceo-tab-days">Day-wise Financials</TabsTrigger>
                  <TabsTrigger value="teams" data-testid="ceo-tab-teams">Team-wise Financials</TabsTrigger>
                  <TabsTrigger value="billing" data-testid="ceo-tab-billing">Billing</TabsTrigger>
                </TabsList>

                <TabsContent value="lifecycle" className="mt-3" data-testid="ceo-detail-lifecycle">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase text-slate-500">Material Type:</span>
                    {Object.entries(detail.materials.by_type || {}).map(([k, v]) => (
                      <button key={k} type="button" data-testid={`ceo-type-filter-${k}`} onClick={() => setTypeFilter(typeFilter === k ? null : k)}>
                        <Badge variant={typeFilter === k ? "default" : "outline"} className={`cursor-pointer text-xs ${typeFilter === k ? "bg-teal-700 hover:bg-teal-800" : "hover:bg-teal-50"}`}>{k}: {num(v)}</Badge>
                      </button>
                    ))}
                    {typeFilter && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-slate-500" data-testid="ceo-type-clear" onClick={() => setTypeFilter(null)}>Clear</Button>}
                  </div>
                  {(() => {
                    const lc = typeFilter ? (detail.materials.by_status_type?.[typeFilter] || {}) : detail.materials.by_status;
                    const lcTotal = typeFilter ? Object.values(lc).reduce((a, b) => a + b, 0) : detail.materials.total;
                    return (
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-left text-sm">
                      <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500"><th className="px-3 py-2">Status{typeFilter ? ` — ${typeFilter}` : ""}</th><th className="px-3 py-2 text-right">Count</th></tr></thead>
                      <tbody>
                        {orderedKeys(lc).map((s) => (
                          <tr key={s} className="border-b last:border-0" data-testid={`ceo-lc-row-${s}`}><td className="px-3 py-1.5">{lbl(s)}</td><td className="px-3 py-1.5 text-right">{num(lc[s])}</td></tr>
                        ))}
                        {!orderedKeys(lc).length && <tr><td colSpan={2} className="px-3 py-4 text-center text-slate-400">No materials for this type.</td></tr>}
                      </tbody>
                      <tfoot><tr className="border-t-2 bg-slate-50 text-xs font-bold"><td className="px-3 py-2">Total{typeFilter ? ` (${typeFilter})` : ""}</td><td className="px-3 py-2 text-right">{num(lcTotal)}</td></tr></tfoot>
                    </table>
                  </div>
                    );
                  })()}
                </TabsContent>

                <TabsContent value="days" className="mt-3" data-testid="ceo-detail-days">
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-left text-sm">
                      <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500"><th className="px-3 py-2">Date</th><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Margin</th><th className="px-3 py-2 text-center">P/L</th></tr></thead>
                      <tbody>
                        {(detail.financial.days || []).map((d) => (
                          <tr key={d.date} className="border-b last:border-0">
                            <td className="px-3 py-1.5">{d.date}</td>
                            <td className="px-3 py-1.5 text-right text-teal-700">{inr(d.revenue)}</td>
                            <td className="px-3 py-1.5 text-right text-slate-600">{inr(d.cost)}</td>
                            <td className={`px-3 py-1.5 text-right ${Number(d.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(d.margin)}</td>
                            <td className="px-3 py-1.5 text-center"><PL m={d.margin} /></td>
                          </tr>
                        ))}
                        {!detail.financial.days.length && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No DPR recorded in this range.</td></tr>}
                      </tbody>
                      {detail.financial.days.length > 0 && (
                        <tfoot><tr className="border-t-2 bg-slate-50 text-xs font-bold"><td className="px-3 py-2">Total</td><td className="px-3 py-2 text-right text-teal-700">{inr(g.revenue)}</td><td className="px-3 py-2 text-right">{inr(g.cost)}</td><td className={`px-3 py-2 text-right ${Number(g.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(g.margin)}</td><td /></tr></tfoot>
                      )}
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="teams" className="mt-3" data-testid="ceo-detail-teams">
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-left text-sm">
                      <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500"><th className="px-3 py-2">Team</th><th className="px-3 py-2 text-right">Activities</th><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Margin</th><th className="px-3 py-2 text-center">P/L</th></tr></thead>
                      <tbody>
                        {(detail.financial.teams || []).map((tm) => (
                          <tr key={tm.team_name} className="border-b last:border-0">
                            <td className="px-3 py-1.5">{tm.team_name}{tm.team_type ? <span className="ml-1 text-xs text-slate-400">({tm.team_type})</span> : null}</td>
                            <td className="px-3 py-1.5 text-right">{num(tm.activity_total)}</td>
                            <td className="px-3 py-1.5 text-right text-teal-700">{inr(tm.revenue)}</td>
                            <td className="px-3 py-1.5 text-right text-slate-600">{inr(tm.cost)}</td>
                            <td className={`px-3 py-1.5 text-right ${Number(tm.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(tm.margin)}</td>
                            <td className="px-3 py-1.5 text-center"><PL m={tm.margin} /></td>
                          </tr>
                        ))}
                        {!detail.financial.teams.length && <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">No team activity in this range.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="billing" className="mt-3" data-testid="ceo-detail-billing">
                  <div className="mb-3 grid grid-cols-2 gap-2">
                    <div className="rounded-lg border bg-emerald-50 p-3 text-center"><div className="text-lg font-bold text-emerald-700">{num(detail.billing.billed)}</div><div className="text-[10px] uppercase text-slate-500">Billed</div></div>
                    <div className="rounded-lg border bg-amber-50 p-3 text-center"><div className="text-lg font-bold text-amber-700">{num(detail.billing.pending_billing)}</div><div className="text-[10px] uppercase text-slate-500">Pending Billing</div></div>
                  </div>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-left text-sm">
                      <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500"><th className="px-3 py-2">Billing Month</th><th className="px-3 py-2 text-right">Billed Materials</th></tr></thead>
                      <tbody>
                        {Object.entries(detail.billing.by_month || {}).map(([m, n]) => (
                          <tr key={m} className="border-b last:border-0"><td className="px-3 py-1.5">{m}</td><td className="px-3 py-1.5 text-right">{num(n)}</td></tr>
                        ))}
                        {!Object.keys(detail.billing.by_month || {}).length && <tr><td colSpan={2} className="px-3 py-4 text-center text-slate-400">No billing records yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
